import React from 'react';
import type {
    SectionPlacement,
    SectionPlacementItem,
} from '../../../stores/section-placements';

type UsePlacementOrdersOptions = {
    placements: SectionPlacement[];
    isFetching: boolean;
    setPlacementItems: (
        placementId: number,
        items: SectionPlacementItem[]
    ) => void;
    sortPlacementItems: (
        placementId: number,
        itemIds: number[]
    ) => Promise<boolean>;
};

export const usePlacementOrders = (options: UsePlacementOrdersOptions) => {
    const {
        placements,
        isFetching,
        setPlacementItems,
        sortPlacementItems,
    } = options;
    const [initialOrders, setInitialOrders] = React.useState<
        Record<number, string>
    >({});

    const getPlacementOrder = React.useCallback(
        (placement: SectionPlacement) =>
            JSON.stringify(placement.items.map((item) => item.id)),
        []
    );

    const replaceInitialOrders = React.useCallback(
        (placementsToSync: SectionPlacement[]) => {
            setInitialOrders(
                placementsToSync.reduce<Record<number, string>>(
                    (next, placement) => {
                        next[placement.id] = getPlacementOrder(placement);
                        return next;
                    },
                    {}
                )
            );
        },
        [getPlacementOrder]
    );

    React.useEffect(() => {
        if (isFetching) return;

        setInitialOrders((prev) => {
            const next = { ...prev };
            placements.forEach((placement) => {
                if (!next[placement.id]) {
                    next[placement.id] = getPlacementOrder(placement);
                }
            });
            return next;
        });
    }, [getPlacementOrder, isFetching, placements]);

    const hasPlacementChanges = React.useCallback(
        (placement: SectionPlacement) => {
            const initialOrder = initialOrders[placement.id];
            if (!initialOrder) return false;
            return getPlacementOrder(placement) !== initialOrder;
        },
        [getPlacementOrder, initialOrders]
    );

    const resetPlacementItems = React.useCallback(() => {
        placements.forEach((placement) => {
            const initialOrder = initialOrders[placement.id];
            if (!initialOrder) return;

            let orderIds: number[] | null = null;
            try {
                const parsed = JSON.parse(initialOrder);
                if (Array.isArray(parsed)) {
                    orderIds = parsed.filter(
                        (id): id is number => typeof id === 'number'
                    );
                }
            } catch {
                orderIds = null;
            }

            if (!orderIds) return;

            const orderIndex = new Map(
                orderIds.map((id, index) => [id, index])
            );
            const nextItems = [...placement.items].sort((a, b) => {
                const aIndex = orderIndex.get(a.id);
                const bIndex = orderIndex.get(b.id);
                if (aIndex == null && bIndex == null) return 0;
                if (aIndex == null) return 1;
                if (bIndex == null) return -1;
                return aIndex - bIndex;
            });
            const currentOrder = JSON.stringify(
                placement.items.map((item) => item.id)
            );
            if (currentOrder !== initialOrder) {
                setPlacementItems(placement.id, nextItems);
            }
        });
    }, [initialOrders, placements, setPlacementItems]);

    const updateInitialOrder = React.useCallback(
        (placementId: number, items: SectionPlacementItem[]) => {
            setInitialOrders((prev) => ({
                ...prev,
                [placementId]: JSON.stringify(items.map((item) => item.id)),
            }));
        },
        []
    );

    const savePlacements = React.useCallback(
        async (placementsToSave: SectionPlacement[]) => {
            for (const placement of placementsToSave) {
                const ok = await sortPlacementItems(
                    placement.id,
                    placement.items.map((item) => item.id)
                );
                if (ok) {
                    setInitialOrders((prev) => ({
                        ...prev,
                        [placement.id]: getPlacementOrder(placement),
                    }));
                }
            }
        },
        [getPlacementOrder, sortPlacementItems]
    );

    return {
        hasPlacementChanges,
        resetPlacementItems,
        replaceInitialOrders,
        updateInitialOrder,
        savePlacements,
    };
};
